import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { PhotoIcon, XMarkIcon, ArrowDownTrayIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import AsyncSelect from 'react-select/async';
import Breadcrumb from '../../../components/Breadcrumb';
import Page from '../../../components/Page';
import ClipboardButton from '../../../components/ClipboardButton';
import client from '../../../lib/feathers';
import PassportAnalyticsPanel from '../../../components/listingDetail/Passports/Analytics/PassportAnalyticsPanel';

// Categories eligible to be added to a passport. Was "Winery" only, which hid
// vineyards / tasting rooms / wine bars that are perfectly valid passport stops
// (e.g. "Potter's Vineyard" is categorized Vineyard, not Winery, so it never
// showed up). These are the wine-venue capability categories; non-wine places
// (Restaurant, Hotel, Distillery, …) stay filtered out.
const PASSPORT_LISTING_CATEGORIES = new Set([
  'Winery', 'Vineyard', 'Tasting Room', 'Wine Bar', 'Wine Shop',
]);

// Drag and drop
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  TouchSensor,
  MouseSensor,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableRow } from '../../../components/listingDetail/SortableItem';

export default function PassportDetail() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const [passport, setPassport] = useState(null);
  const [listings, setListings] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [organizers, setOrganizers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditListingModal, setShowEditListingModal] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [editFormData, setEditFormData] = useState({ subtitle: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [showAddListingModal, setShowAddListingModal] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [addingListing, setAddingListing] = useState(false);
  const [participantFilter, setParticipantFilter] = useState('all'); // 'all' | 'completed'
  const [analyticsListingId, setAnalyticsListingId] = useState(null);
  const [togglingAnalytics, setTogglingAnalytics] = useState(null); // listingPassportId being toggled
  const logoInputRef = useRef(null);

  // Drag and drop sensors
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 2,
    },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 100,
      tolerance: 5,
    },
  });

  const sensors = useSensors(mouseSensor, touchSensor);

  const updateListingOrder = async (reorderedListings) => {
    for (let i = 0; i < reorderedListings.length; i++) {
      try {
        await client.service('admin/listing-passport').patch(reorderedListings[i].id, { order: i + 1 });
      } catch (e) {
        console.error('Error updating order:', e);
      }
    }
  };

  const handleListingDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = listings.findIndex((lp) => lp.id === active.id);
    const newIndex = listings.findIndex((lp) => lp.id === over.id);
    const reorderedListings = arrayMove(listings, oldIndex, newIndex);

    setListings(reorderedListings);
    updateListingOrder(reorderedListings);
  };

  const getPublicUrl = () => {
    const baseUrl = `${process.env.REACT_APP_CLIENT_URL}/passport/${pid}`;
    if (passport?.status === 'draft' && passport?.updatedAt) {
      const previewToken = new Date(passport.updatedAt).getTime();
      return `${baseUrl}?preview=${previewToken}`;
    }
    return baseUrl;
  };

  const loadQrCode = async () => {
    try {
      const service = client.service('admin/qr-code');
      const code = await service.create({ url: getPublicUrl() });
      setQrCode(code);
    } catch (e) {
      console.error('Error generating QR code:', e);
    }
  };

  const loadPassport = async () => {
    try {
      setLoading(true);
      const service = client.service('admin/passport');
      const result = await service.get(pid);
      setPassport(result);
    } catch (e) {
      console.error('Error loading passport:', e);
      setError('Failed to load passport');
    } finally {
      setLoading(false);
    }
  };

  const loadListings = async () => {
    try {
      const service = client.service('admin/listing-passport');
      const result = await service.find({
        query: {
          passportId: pid,
          $sort: { order: 1 },
        },
      });
      setListings(result.data || []);
    } catch (e) {
      console.error('Error loading listings:', e);
    }
  };

  const loadParticipants = async () => {
    try {
      const service = client.service('admin/passport-participants');
      const result = await service.find({
        query: { passportId: pid },
      });
      setParticipants(result.data || []);
    } catch (e) {
      console.error('Error loading participants:', e);
    }
  };

  const loadOrganizers = async () => {
    try {
      const service = client.service('admin/passport-user');
      const result = await service.find({
        query: {
          passportId: pid,
          role: 'organizer',
        },
      });
      setOrganizers(result.data || []);
    } catch (e) {
      console.error('Error loading organizers:', e);
    }
  };

  const handleDeletePassport = async () => {
    if (!window.confirm('Are you sure you want to delete this passport? This action cannot be undone.')) {
      return;
    }
    try {
      await client.service('admin/passport').remove(pid);
      navigate('/admin/passports');
    } catch (e) {
      console.error('Error deleting passport:', e);
      alert('Failed to delete passport');
    }
  };

  const exportParticipants = async () => {
    try {
      const service = client.service('admin/passport-participants');
      const result = await service.get(pid);

      // Download CSV
      const blob = new Blob([result.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `passport-${passport.title.replace(/\s+/g, '-')}-participants.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error exporting participants:', e);
      alert('Failed to export participants');
    }
  };

  const handleEditListing = (listing) => {
    setEditingListing(listing);
    setEditFormData({
      subtitle: listing.subtitle || '',
      description: listing.description || '',
    });
    // Set logo preview if listing has a logo (custom or from listing)
    const logoPath = listing.logo?.path || listing.listing?.logoAttachment?.path;
    if (logoPath) {
      setLogoPreview(`${process.env.REACT_APP_ATTACHMENT_URL}/${logoPath}?w=300&f=inside`);
    } else {
      setLogoPreview(null);
    }
    setLogoFile(null);
    setShowEditListingModal(true);
  };

  // Helper to check if listing has a custom passport logo
  const hasCustomLogo = (listing) => !!listing?.logo;

  const handleLogoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, or WebP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const uploadLogo = async (listingPassportId, listingId) => {
    if (!logoFile) return;

    setUploadingLogo(true);
    try {
      // Step 1: Get signed URL
      const uploadService = client.service('admin/listing-passport-logo-upload');
      const { signedRequest, path } = await uploadService.create({
        passportId: pid,
        listingId,
        'file-type': logoFile.type
      });

      // Step 2: Upload to S3
      await fetch(signedRequest, {
        method: 'PUT',
        body: logoFile,
        headers: {
          'Content-Type': logoFile.type
        }
      });

      // Step 3: Persist the attachment
      await uploadService.patch(listingPassportId, {
        path,
        mime: logoFile.type
      });

      return true;
    } catch (e) {
      console.error('Error uploading logo:', e);
      throw e;
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!editingListing) return;

    // If there's no existing logo (just a preview from file selection), just clear it
    if (!editingListing.logo) {
      setLogoFile(null);
      setLogoPreview(null);
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
      return;
    }

    if (!window.confirm('Are you sure you want to remove the custom logo?')) return;

    setSaving(true);
    try {
      const uploadService = client.service('admin/listing-passport-logo-upload');
      await uploadService.remove(editingListing.id);
      setLogoFile(null);
      setLogoPreview(null);
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
      // Update the local state
      setEditingListing({ ...editingListing, logo: null });
      loadListings();
    } catch (e) {
      console.error('Error removing logo:', e);
      alert(e.message || 'Failed to remove logo');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveListingEdit = async () => {
    if (!editingListing) return;
    setSaving(true);
    try {
      // Save subtitle and description
      await client.service('admin/listing-passport').patch(editingListing.id, {
        subtitle: editFormData.subtitle || null,
        description: editFormData.description || null,
      });

      // Upload logo if a new file was selected
      if (logoFile) {
        await uploadLogo(editingListing.id, editingListing.listingId);
      }

      setShowEditListingModal(false);
      setEditingListing(null);
      setLogoFile(null);
      setLogoPreview(null);
      loadListings();
    } catch (e) {
      console.error('Error saving listing:', e);
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Search listings for the add listing modal (filtered to wineries only)
  const searchListings = async (inputValue, callback) => {
    if (!inputValue || inputValue.length < 2) {
      callback([]);
      return;
    }

    try {
      const service = client.service('admin/listing');
      const result = await service.find({
        query: {
          name: { $iLike: `%${inputValue}%` },
          $sort: { name: 1 },
          $limit: 50, // Fetch more to account for filtering
          isExcluded: false,
        },
      });

      // Filter out listings already in the passport
      const existingListingIds = listings.map((lp) => lp.listingId);

      // Filter to wine venues (winery / vineyard / tasting room / wine bar /
      // wine shop) and exclude listings already in the passport.
      const availableListings = result.data.filter(
        (l) => !existingListingIds.includes(l.id)
          && l.categories?.some((c) => PASSPORT_LISTING_CATEGORIES.has(c.name))
      );

      callback(
        availableListings.slice(0, 20).map((l) => ({
          value: l.id,
          label: l.name,
        }))
      );
    } catch (e) {
      console.error('Error searching listings:', e);
      callback([]);
    }
  };

  const handleAddListing = async () => {
    if (!selectedListing) return;

    setAddingListing(true);
    try {
      await client.service('admin/listing-passport').create({
        passportId: pid,
        listingId: selectedListing.value,
        order: listings.length + 1,
      });

      setShowAddListingModal(false);
      setSelectedListing(null);
      loadListings();
    } catch (e) {
      console.error('Error adding listing:', e);
      alert(e.message || 'Failed to add listing');
    } finally {
      setAddingListing(false);
    }
  };

  const handleToggleEnhancedAnalytics = async (listingPassport) => {
    const newValue = !listingPassport.enhancedAnalyticsEnabled;
    setTogglingAnalytics(listingPassport.id);
    try {
      await client.service('admin/listing-passport').patch(listingPassport.id, {
        enhancedAnalyticsEnabled: newValue,
      });
      loadListings();
    } catch (e) {
      console.error('Error toggling enhanced analytics:', e);
      alert(e.message || 'Failed to update enhanced analytics setting');
    } finally {
      setTogglingAnalytics(null);
    }
  };

  useEffect(() => {
    loadPassport();
    loadListings();
    loadOrganizers();
  }, [pid]);

  useEffect(() => {
    if (passport) {
      loadQrCode();
    }
  }, [passport?.updatedAt, passport?.status]);

  useEffect(() => {
    if (activeTab === 'participants') {
      loadParticipants();
    }
  }, [activeTab]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      active: 'bg-green-100 text-green-800',
      ended: 'bg-yellow-100 text-yellow-800',
      archived: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <Page title="Loading...">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Page>
    );
  }

  if (error || !passport) {
    return (
      <Page title="Error">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error || 'Passport not found'}</p>
        </div>
      </Page>
    );
  }

  // Group tabs: "About" tabs vs "Manage" tabs
  const aboutTabs = [
    { id: 'overview', label: 'Overview' },
  ];
  const manageTabs = [
    { id: 'listings', label: `Listings (${listings.length})` },
    { id: 'participants', label: 'Participants' },
    { id: 'organizers', label: `Organizers (${organizers.length})` },
    { id: 'analytics', label: 'Analytics', icon: ChartBarIcon },
  ];

  return (
    <Page title={passport.title}>
      <div className="pb-5 border-b border-gray-200">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[
            { name: 'Passports', href: '/admin/passports' },
            { name: passport.title }
          ]} />
        </nav>
      </div>

      {/* Header */}
      <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          {/* Title and Actions Row */}
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">{passport.title}</h3>
            <div className="flex items-center gap-3">
              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(passport.status)}`}>
                {passport.status}
              </span>
              <Link
                to={`/admin/passports/${pid}/edit`}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Edit
              </Link>
            </div>
          </div>

          {/* Two Column Layout: Cover Image/Description | QR Code */}
          <div className="md:grid md:grid-cols-3 md:gap-6">
            {/* Left Column: Cover Image and Description */}
            <div className="md:col-span-2">
              {passport.coverImage && (
                <div style={{ maxWidth: 375 }}>
                  <img
                    src={`${process.env.REACT_APP_ATTACHMENT_URL}/${passport.coverImage.path}`}
                    alt="Passport cover"
                    className="w-full object-cover rounded-lg"
                  />
                </div>
              )}

              {passport.description && (
                <div className="mt-3 text-sm text-gray-500" style={{ maxWidth: 375 }}>
                  <Markdown rehypePlugins={[rehypeRaw]} className="[&_a]:underline [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4">
                    {passport.description}
                  </Markdown>
                </div>
              )}
            </div>

            {/* Right Column: QR Code and Public Link */}
            <div className="mt-5 md:mt-0 flex flex-col items-center">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Public QR Code</h4>
              {qrCode ? (
                <div className="flex flex-col items-center w-full max-w-[224px]">
                  <div className="bg-white p-4 border border-gray-200 rounded-lg w-full">
                    <img src={qrCode} alt="Passport QR Code" className="w-full aspect-square" />
                  </div>
                  <a
                    href={qrCode}
                    download={`${passport.title.replace(/\s+/g, '_')}_qrcode.png`}
                    className="mt-3 inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    Download QR Code
                  </a>
                </div>
              ) : (
                <div className="w-full max-w-[224px] aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              )}

              {/* Public Link */}
              <div className="mt-4 text-center">
                <p className="text-xs text-gray-500 mb-2">
                  {passport.status === 'draft' ? 'Preview Link' : 'Public Link'}
                </p>
                <div className="flex items-center gap-2">
                  <a
                    href={getPublicUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:text-red-700 underline break-all"
                  >
                    {passport.status === 'draft' ? 'Preview Passport' : 'View Passport'}
                  </a>
                  <ClipboardButton text={getPublicUrl()} />
                </div>
                {passport.status === 'draft' && (
                  <p className="text-xs text-amber-600 mt-1">
                    This preview link will expire when the passport is edited.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex items-center">
            {/* About tabs */}
            <div className="flex space-x-8">
              {aboutTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Separator */}
            <div className="mx-6 h-6 w-px bg-gray-300" />

            {/* Manage tabs */}
            <div className="flex space-x-8">
              {manageTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === 'overview' && (
            <>
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="border-t border-gray-200">
                <dl>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Date Range</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {formatDate(passport.startDate)} - {formatDate(passport.endDate)}
                    </dd>
                  </div>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Completion Milestones</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {(() => {
                        const thresholds = passport.completionThreshold?.toString().split(',').map((t) => t.trim());
                        if (thresholds?.length > 1) {
                          return (
                            <div>
                              <span className="font-medium">{thresholds[0]} stops</span>
                              <span className="text-gray-500"> to complete</span>
                              <br />
                              <span className="text-gray-500 text-xs">
                                Bonus milestones: {thresholds.slice(1).join(', ')} stops
                              </span>
                            </div>
                          );
                        }
                        return `${passport.completionThreshold} stops`;
                      })()}
                    </dd>
                  </div>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Ask Membership</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {passport.askMembership ? 'Yes' : 'No'}
                    </dd>
                  </div>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Opt-In Copy</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {passport.optInCopy || '(Using default)'}
                    </dd>
                  </div>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Created By</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {passport.createdBy
                        ? `${passport.createdBy.firstName} ${passport.createdBy.lastName} (${passport.createdBy.email})`
                        : 'N/A'}
                    </dd>
                  </div>
                                  </dl>
              </div>
            </div>

            {/* Danger Zone */}
            <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg border border-red-200">
              <div className="px-4 py-5 sm:px-6">
                <h4 className="text-lg font-medium text-red-600">Danger Zone</h4>
              </div>
              <div className="border-t border-red-200 px-4 py-5 sm:px-6">
                <button
                  onClick={handleDeletePassport}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                >
                  Delete Passport
                </button>
              </div>
            </div>
            </>
          )}

          {activeTab === 'listings' && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                <div>
                  <h4 className="text-lg font-medium text-gray-900">Listings in Passport</h4>
                  <p className="text-sm text-gray-500">Drag rows to reorder</p>
                </div>
                <button
                  onClick={() => setShowAddListingModal(true)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Add Listing
                </button>
              </div>
              <DndContext
                onDragEnd={handleListingDragEnd}
                collisionDetection={closestCenter}
                sensors={sensors}
              >
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Listing</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Accepted</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Enhanced Analytics</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <SortableContext
                    items={listings.map((lp) => lp.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <tbody className="bg-white divide-y divide-gray-200">
                      {listings.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-6 py-12 text-center text-sm text-gray-500">
                            No listings added yet.
                          </td>
                        </tr>
                      ) : (
                        listings.map((lp, index) => (
                          <SortableRow key={lp.id} id={lp.id} index={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 cursor-move">
                              {index + 1}
                            </td>
                            <td className="px-6 py-4">
                              {(lp.logo || lp.listing?.logoAttachment) && (
                                <img
                                  src={`${process.env.REACT_APP_ATTACHMENT_URL}/${(lp.logo || lp.listing?.logoAttachment).path}?w=300&f=inside`}
                                  alt={`${lp.listing?.name} logo`}
                                  className="h-16 w-auto object-contain mb-2"
                                />
                              )}
                              <div className="text-sm font-medium text-gray-900">
                                {lp.listing?.name || 'Unknown'}
                              </div>
                              {lp.subtitle && (
                                <div className="text-sm text-gray-500">{lp.subtitle}</div>
                              )}
                              {lp.description && (
                                <div className="text-sm text-gray-600 mt-1 prose prose-sm max-w-none">
                                  <Markdown rehypePlugins={[rehypeRaw]}>{lp.description}</Markdown>
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {lp.accepted ? (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                  Accepted
                                </span>
                              ) : (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                  Pending
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                type="button"
                                onClick={() => handleToggleEnhancedAnalytics(lp)}
                                disabled={togglingAnalytics === lp.id}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                                  lp.enhancedAnalyticsEnabled ? 'bg-primary' : 'bg-gray-200'
                                } ${togglingAnalytics === lp.id ? 'opacity-50' : ''}`}
                                role="switch"
                                aria-checked={lp.enhancedAnalyticsEnabled}
                              >
                                <span
                                  aria-hidden="true"
                                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                    lp.enhancedAnalyticsEnabled ? 'translate-x-5' : 'translate-x-0'
                                  }`}
                                />
                              </button>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                              <button
                                onClick={() => handleEditListing(lp)}
                                className="text-primary hover:text-red-900"
                              >
                                Edit
                              </button>
                              <Link to={`/admin/listings/${lp.listingId}`} className="text-gray-600 hover:text-gray-900">
                                View Listing
                              </Link>
                            </td>
                          </SortableRow>
                        ))
                      )}
                    </tbody>
                  </SortableContext>
                </table>
              </DndContext>
            </div>
          )}

          {activeTab === 'participants' && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <h4 className="text-lg font-medium text-gray-900">Opted-In Participants</h4>
                  <div className="flex rounded-md shadow-sm">
                    <button
                      type="button"
                      onClick={() => setParticipantFilter('all')}
                      className={`px-3 py-1 text-sm font-medium rounded-l-md border ${
                        participantFilter === 'all'
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      onClick={() => setParticipantFilter('completed')}
                      className={`px-3 py-1 text-sm font-medium rounded-r-md border-t border-r border-b ${
                        participantFilter === 'completed'
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Completed
                    </button>
                  </div>
                </div>
                <button
                  onClick={exportParticipants}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Export CSV
                </button>
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member Of</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined Club</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opted In</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(() => {
                    const filteredParticipants = participantFilter === 'completed'
                      ? participants.filter(p => p.progress?.completed)
                      : participants;

                    if (filteredParticipants.length === 0) {
                      return (
                        <tr>
                          <td colSpan="5" className="px-6 py-12 text-center text-sm text-gray-500">
                            {participantFilter === 'completed'
                              ? 'No completed participants yet.'
                              : 'No participants yet.'}
                          </td>
                        </tr>
                      );
                    }

                    return filteredParticipants.map((p) => {
                      // Resolve member listing names (at opt-in time)
                      const memberAtOptInNames = (p.memberListingIds || [])
                        .map(id => listings.find(lp => lp.listingId === id)?.listing?.name)
                        .filter(Boolean);

                      // Resolve joined during passport listing names
                      const joinedDuringNames = (p.joinedClubListingIds || [])
                        .map(id => listings.find(lp => lp.listingId === id)?.listing?.name)
                        .filter(Boolean);

                      // Calculate milestones display
                      const thresholds = p.progress?.thresholds || [];
                      const milestonesReached = p.progress?.milestonesReached || 0;

                      return (
                        <tr key={p.id}>
                          <td className="px-6 py-4">
                            {p.user ? (
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {p.user.firstName} {p.user.lastName}
                                </div>
                                <div className="text-sm text-gray-500">{p.user.email}</div>
                              </div>
                            ) : (
                              'Unknown'
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {p.progress?.visited || 0} / {p.progress?.total || 0} stops
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              {p.progress?.completed && (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                  Completed
                                </span>
                              )}
                              {thresholds.length > 1 && (
                                <span className="px-2 inline-flex text-xs leading-5 font-medium rounded-full bg-blue-100 text-blue-800">
                                  {milestonesReached}/{thresholds.length} milestones
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {memberAtOptInNames.length > 0 ? (
                              <div className="text-sm text-gray-900">
                                {memberAtOptInNames.map((name, idx) => (
                                  <div key={idx}>{name}</div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {joinedDuringNames.length > 0 ? (
                              <div className="text-sm text-gray-900">
                                {joinedDuringNames.map((name, idx) => (
                                  <div key={idx}>{name}</div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(p.optedInAt)}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'organizers' && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                <h4 className="text-lg font-medium text-gray-900">Organizers</h4>
                <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  Add Organizer
                </button>
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {organizers.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="px-6 py-12 text-center text-sm text-gray-500">
                        No organizers added yet.
                      </td>
                    </tr>
                  ) : (
                    organizers.map((org) => (
                      <tr key={org.id}>
                        <td className="px-6 py-4">
                          {org.user ? (
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {org.user.firstName} {org.user.lastName}
                              </div>
                              <div className="text-sm text-gray-500">{org.user.email}</div>
                            </div>
                          ) : (
                            'Unknown'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(org.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button className="text-red-600 hover:text-red-900">Remove</button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div>
              {/* Listing selector for admins */}
              {listings.length > 0 && (
                <div className="mb-4 bg-white shadow rounded-lg p-4">
                  <label htmlFor="analytics-listing" className="block text-sm font-medium text-gray-700 mb-2">
                    Select Listing to View Analytics
                  </label>
                  <select
                    id="analytics-listing"
                    value={analyticsListingId || ''}
                    onChange={(e) => setAnalyticsListingId(e.target.value || null)}
                    className="block w-full max-w-md border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  >
                    <option value="">Select a listing...</option>
                    {listings.map((lp) => (
                      <option key={lp.id} value={lp.listingId}>
                        {lp.listing?.name || 'Unknown Listing'}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {analyticsListingId ? (
                <PassportAnalyticsPanel
                  passportId={pid}
                  listingId={analyticsListingId}
                  isAdmin
                />
              ) : (
                <div className="bg-white shadow rounded-lg p-12 text-center">
                  <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No listing selected</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Select a listing above to view its analytics for this passport.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit Listing Modal */}
      {showEditListingModal && editingListing && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowEditListingModal(false)} />

            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Edit Listing Details
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                {editingListing.listing?.name}
              </p>

              <div className="space-y-4">
                <div>
                  <label htmlFor="subtitle" className="block text-sm font-medium text-gray-700 mb-1">
                    Subtitle
                  </label>
                  <input
                    type="text"
                    id="subtitle"
                    value={editFormData.subtitle}
                    onChange={(e) => setEditFormData({ ...editFormData, subtitle: e.target.value })}
                    placeholder="Optional subtitle for this passport"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">A short tagline shown with the listing name</p>
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="description"
                    rows={4}
                    value={editFormData.description}
                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    placeholder="Optional description for this listing in the passport"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">Supports markdown formatting</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Logo
                  </label>
                  {logoPreview ? (
                    <div>
                      <div className="relative inline-block">
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="h-24 w-auto object-contain rounded-lg border border-gray-200 cursor-pointer"
                          onClick={() => logoInputRef.current?.click()}
                          title="Click to replace"
                        />
                        {(hasCustomLogo(editingListing) || logoFile) && (
                          <button
                            type="button"
                            onClick={handleRemoveLogo}
                            disabled={saving}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 disabled:opacity-50"
                            title="Remove custom logo"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      {!hasCustomLogo(editingListing) && !logoFile && (
                        <p className="mt-1 text-xs text-blue-600">Using logo from listing</p>
                      )}
                    </div>
                  ) : (
                    <div
                      onClick={() => logoInputRef.current?.click()}
                      className="flex items-center justify-center h-24 w-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 hover:bg-gray-50"
                    >
                      <PhotoIcon className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleLogoFileChange}
                    className="hidden"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Click image to upload a custom logo (JPEG, PNG, or WebP)
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditListingModal(false);
                    setEditingListing(null);
                    setLogoFile(null);
                    setLogoPreview(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveListingEdit}
                  disabled={saving || uploadingLogo}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700 disabled:opacity-50"
                >
                  {uploadingLogo ? 'Uploading...' : saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Listing Modal */}
      {showAddListingModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowAddListingModal(false)} />

            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Add Listing to Passport
              </h2>

              <div className="mb-4">
                <label htmlFor="listing-search" className="block text-sm font-medium text-gray-700 mb-1">
                  Search for a listing
                </label>
                <AsyncSelect
                  id="listing-search"
                  cacheOptions
                  loadOptions={searchListings}
                  value={selectedListing}
                  onChange={setSelectedListing}
                  placeholder="Type to search listings..."
                  isClearable
                  noOptionsMessage={({ inputValue }) =>
                    inputValue.length < 2
                      ? 'Type at least 2 characters to search'
                      : 'No listings found'
                  }
                />
                <p className="mt-1 text-xs text-gray-500">
                  Listings already in this passport are excluded from results
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddListingModal(false);
                    setSelectedListing(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddListing}
                  disabled={!selectedListing || addingListing}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingListing ? 'Adding...' : 'Add Listing'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
